import type { CSSProperties } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { RfmItem, RfmTemplateItem } from "@/types/rfm";
import { segmentIcons } from "./rfmSegmentIcons";

type RfmAnalysisChartProps = {
  items: RfmItem[];
  template: RfmTemplateItem[];
  selectedSegmentId: number | null;
  onSelectSegment: (segmentId: number) => void;
};

type SegmentTheme = {
  className: string;
};

const segmentThemes: Record<string, SegmentTheme> = {
  Critical: { className: "bg-rose-400 text-rose-50" },
  Loyalist: { className: "bg-violet-500 text-violet-50" },
  Champion: { className: "bg-blue-500 text-blue-50" },
  "At risk": { className: "bg-orange-400 text-orange-50" },
  Average: { className: "bg-amber-300 text-amber-950" },
  "Potential loyalist": { className: "bg-teal-400 text-teal-50" },
  Hibernating: { className: "bg-zinc-400 text-zinc-50" },
  "About to sleep": { className: "bg-sky-500 text-sky-50" },
  Promising: { className: "bg-pink-400 text-pink-50" },
  New: { className: "bg-lime-400 text-lime-50" },
};

const getGridPlacementStyle = (segment: RfmTemplateItem): CSSProperties => {
  if (segment.recencyScores.length === 0 || segment.frequencyMonetaryCombinedScores.length === 0) {
    return {};
  }

  const minRecency = Math.min(...segment.recencyScores);
  const maxRecency = Math.max(...segment.recencyScores);
  const minFrequencyMonetary = Math.min(...segment.frequencyMonetaryCombinedScores);
  const maxFrequencyMonetary = Math.max(...segment.frequencyMonetaryCombinedScores);

  return {
    gridColumn: `${minRecency} / ${maxRecency + 1}`,
    gridRow: `${6 - maxFrequencyMonetary} / ${7 - minFrequencyMonetary}`,
  };
};

export const RfmAnalysisChart = ({ items, template, selectedSegmentId, onSelectSegment }: RfmAnalysisChartProps) => {
  const itemById = new Map(items.map(item => [item.segmentId, item]));

  return (
    <section aria-labelledby="rfm-chart-title" className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 id="rfm-chart-title" className="text-xl font-semibold text-slate-800">RFM segments</h2>
      <div className="mt-6 flex items-start gap-3">
        <div className="flex items-center self-stretch pb-11 text-xs font-semibold text-slate-500">
          <span className="inline-block [writing-mode:vertical-rl] rotate-180">Frequency and monetary score</span>
        </div>
        <div className="grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
          <div className="grid grid-rows-[repeat(5,minmax(52px,1fr))] text-center text-xs font-semibold text-slate-500 md:grid-rows-[repeat(5,minmax(64px,1fr))]">
            {[5, 4, 3, 2, 1].map(score => <span key={score} className="flex items-center justify-center">{score}</span>)}
          </div>
          <div className="min-w-0">
            <div className="grid min-w-[340px] grid-cols-[repeat(5,minmax(64px,1fr))] grid-rows-[repeat(5,minmax(52px,1fr))] gap-1 md:grid-rows-[repeat(5,minmax(64px,1fr))]">
              {template.map(segment => {
                const item = itemById.get(segment.segmentId);
                const theme = segmentThemes[segment.segmentName] ?? { className: "bg-slate-400 text-white" };
                const placementStyle = getGridPlacementStyle(segment);
                const isSelected = selectedSegmentId === segment.segmentId;

                return (
                  <button
                    key={segment.segmentId}
                    type="button"
                    aria-pressed={isSelected}
                    aria-label={`${segment.segmentName}: ${item?.companyCount ?? 0} companies`}
                    onClick={() => onSelectSegment(segment.segmentId)}
                    style={placementStyle}
                    className={`relative flex min-h-[52px] flex-col items-start rounded-md border px-2 py-2 text-left shadow-sm transition-colors md:min-h-[64px] ${theme.className} ${isSelected ? "border-fuchsia-500 ring-2 ring-fuchsia-400" : "border-white/70 hover:border-slate-200"}`}
                  >
                    <span className="text-[10px] font-semibold md:text-xs">{segment.segmentName}</span>
                    <span className="flex items-baseline gap-1">
                      <span className="text-[10px] font-semibold md:text-xs">{item?.companyCount ?? 0}</span>
                      <span className="text-[10px] font-semibold md:text-xs">({(item?.percentageOfTotalValue1 ?? 0).toFixed(1)}%)</span>
                    </span>
                    {segmentIcons[segment.segmentName] && <FontAwesomeIcon icon={segmentIcons[segment.segmentName]} className="absolute bottom-1.5 right-2 text-white" aria-hidden />}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-5 text-center text-xs font-semibold text-slate-500">{[1, 2, 3, 4, 5].map(score => <span key={score}>{score}</span>)}</div>
            <div className="mt-1 text-center text-xs font-semibold text-slate-500">Recency score</div>
          </div>
        </div>
      </div>
    </section>
  );
};